import { apiFetch, apiPost } from "./APIRepository";
import { IQuestion, IQuestionUpload, IDistractor, INetworkResponse,
    IReportQuestion, IReasonList, IReport, IServerReportFull, IQuestionImport } from "../interfaces/models";
import TopicRepository from "./TopicRepository";

type ISearchResult = { items: IQuestion[], searchResult: any, totalItems: number, page: number };

function toQuestion(x: IQuestion): IQuestion {
    let solution: undefined | IDistractor = x.distractors.find(d => d.isCorrect === true);
    if (solution === undefined) {
        throw new Error(`Question id: ${x.id} does not have a solution`);
    }
    if (x.effectivenessCount && x.effectivenessCount < 3) {
        x.effectiveness = 0;
    }
    const question: IQuestion = {
        id: x.id,
        difficulty: x.difficulty,
        correctness: x.correctness,
        effectiveness: x.effectiveness,
        topics: x.topics.map(t => TopicRepository.topicPointer(t)),
        content: x.content,
        explanation: x.explanation,
        solution: solution,
        distractors: x.distractors,
        comments: x.comments,
        commentCount: x.commentCount,
        responseCount: x.responseCount,
        personalRating: x.personalRating,
        canEdit: x.canEdit,
        createdAt: x.createdAt,
        author: x.author,
        removed: x.removed
    };
    return question;
}

export default class QuestionRepository {
    static uploadQuestion(question: IQuestionUpload, path: string): Promise<IQuestion> {
        return apiPost<{question: IQuestion}>(path, question)
            .then(x => x.question)
            .then(response => toQuestion(response));
    }

    static deleteQuestion(id: number) {
        return apiFetch<{}>(`/questions/delete/${id}/`);
    }

    static getPreviousQuestions(id: number) {
        return apiFetch<IQuestion[]>(`/questions/previous/${id}/`)
            .then(questions => questions.map(toQuestion));
    }

    static search(sortField: string | undefined,
        sortOrder: string | undefined,
        filterField: string | undefined,
        filterTopics: number[] | undefined,
        query: string | undefined,
        page: number | undefined,
        pageSize: number | undefined) {
        return apiPost<ISearchResult>(`/questions/search/`, {
            sortField,
            sortOrder,
            filterField,
            filterTopics,
            query,
            page,
            pageSize
        })
        .then(searchResult => ({
            totalItems: searchResult.totalItems,
            questions: searchResult.items.reduce(function(result: IQuestion[], element: IQuestion) {
                try {
                    result.push(toQuestion(element));
                } catch (e) {
                    console.log(e);
                }
                return result;
            }, []),
            page: searchResult.page
        }));
    }

    static submitResponse(distractorID: number) {
        return apiPost<{}>(`/questions/respond/`, { distractorID });
    }

    static submitRating(questionID: number, rateType: string, rateValue: number) {
        return apiPost<{}>(`/questions/rate/`, {
            questionID: questionID,
            [`${rateType}`]: rateValue
        });
    }

    static submitComment(questionID: number, commentID: number, content: string) {
        return apiPost<IQuestion>(`/questions/submitComment/`, {
            question: questionID,
            comment: commentID,
            content: content
        }).then(toQuestion);
    }

    static editComment(questionID: number, commentID: number, content: string) {
        return apiPost<IQuestion>(`/questions/editComment/`, {
            question: questionID,
            comment: commentID,
            content: content
        }).then(toQuestion);
    }

    static upvoteComment(questionID: number, commentID: number) {
        return apiPost<IQuestion>(`/questions/upvoteComment/`, {
            question: questionID,
            comment: commentID
        }).then(toQuestion);
    }

    static getQuestionDistribution(question: IQuestion): Promise<{[responseId: number]: number}> {
        return apiFetch(`/questions/distribution/${question.id}/`);
    }

    static uploadReport(questionReport: IReportQuestion) {
        return apiPost<INetworkResponse>("/questions/report/", { questionReport });
    }

    static getReportedQuestions(sortField: string, sortOrder: "ASC" | "DESC") {
        return apiFetch<IReport[]>(`/questions/report/all/${sortField}/${sortOrder}/`);
    }

    static resolveReport(id: number) {
        return apiFetch<IReport[]>(`/questions/report/resolve/${id}/`);
    }

    static getQuestionById(questionId: number) {
        return apiFetch<IQuestion>(`/questions/id/${questionId}/`)
            .then(toQuestion);
    }
    static getRandomCourseQuestion() {
        return apiFetch<number>(`/questions/random/`);
    }

    static getNextRecommendedQuestion(id: number) {
        return apiFetch<number>(`/questions/recommended/next/${id}/`);
    }

    static getReportReasons() {
        return apiFetch<IReasonList>("/questions/report/reasons/", {
            method: "POST",
            headers: new Headers({
                "Accept": "application/json",
                "Content-Type": "Application/json"
            })
        }).then(x => x.reasonList);
    }

    static getReportAggregates(reportCount: number) {
        return apiFetch<IServerReportFull[]>(`/questions/report/all/${reportCount}/`);
    }

    static getQuestionsForImport(importParams: IQuestionImport) {
        return apiPost<number>("/questions/import/search/", { importParams });
    }

    static importQuestions(importParams: IQuestionImport) {
        return apiPost<number>("/questions/import/", { importParams });
    }
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader!./src/repositories/QuestionRepository.ts